import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, Alert } from '@dreamtree-org/twreact-ui';
import { Volume2, VolumeX, Sparkles, PhoneCall, PhoneOff } from 'lucide-react';
import MessageBubble from '@/components/MessageBubble';
import MessageActions from '@/components/MessageActions';
import ToolTraceStack from '@/components/ToolTraceStack';
import ChatActionsMenu from '@/components/ChatActionsMenu';
import EmbedHeader from '@/components/EmbedHeader';
import VoiceDock from '@/components/VoiceDock';
import SuggestedPrompts from '@/components/SuggestedPrompts';
import Composer from '@/components/Composer';
import AskUserCard from '@/components/AskUserCard';
import ClientToolConfirmCard from '@/components/ClientToolConfirmCard';
import { applyBrand } from '@/theme/applyTheme';
import { useCharacterSpeech } from '@/lib/useCharacterSpeech';
import { useVoiceMode } from '@/lib/useVoiceMode';
import { useEmbedChat } from '@/lib/useEmbedChat';
import { useHostSync } from '@/lib/useHostSync';
import { useClientToolBridge } from '@/lib/useClientToolBridge';

/**
 * Public Embeddable Chat Widget's page (PRD §3.9, TDD §5.7) — mounted OUTSIDE
 * RequireAuth/DefaultLayout (see App.jsx): no sidebar, no auth check, no cookies sent.
 * A deliberately separate, minimal surface from the authenticated chat UI. With Auto
 * Approve OFF (the default) no `gated` tool runs here and admin approval cards can never
 * fire; with Auto Approve ON the Human Proxy decides non-destructive gated calls inline
 * (destructive ones still deny). Two other human-in-the-loop interactions can fire with
 * the toggle OFF, and both belong to the visitor rather than to an operator:
 *   - ask_user — answering a question about their own request (AskUserCard, resumed over
 *     POST /api/embed/session/:id/answer)
 *   - `confirm` client-provided tools — allowing or denying an action the agent wants to
 *     take in the page they are looking at (ClientToolConfirmCard, resumed over POST
 *     /api/embed/session/:id/tool-result)
 *
 * All session/polling/send/upload/clear state lives in useEmbedChat.js (mirroring how
 * ChatPanel.jsx is a thin wrapper around useAgentChat.js) — this page just renders it.
 * The thread/composer/attach/file-preview UI itself (MessageBubble, MessageActions,
 * Composer, ChatActionsMenu) is shared with the internal chat, not a second copy of it.
 *
 * PANEL CONTROLS. Expand and close live here but act on the HOST page, over the
 * postMessage bridge, and are rendered only when that host announced it understands them —
 * embed.js is a cached static file on someone else's site, so an old copy that would
 * ignore the message must produce no button at all rather than a dead one.
 */
/** The area under the thread: a paused ask_user question card, permission cards for any
 *  client-provided tool the agent wants to run in the host page, else a "thinking…" row
 *  while a turn is in flight — mirrors ChatPanel.jsx's PendingArea. */
function PendingArea({ waitingForUser, pendingJob, sending, showTraces, onAnswer, awaitingConfirm, onDecideConfirm }) {
  if (waitingForUser) return <AskUserCard question={pendingJob.question} onAnswer={onAnswer} />;
  if (awaitingConfirm.length) {
    return (
      <div className="space-y-2">
        {awaitingConfirm.map((call) => (
          <ClientToolConfirmCard key={call.callId} call={call} onDecide={onDecideConfirm} />
        ))}
      </div>
    );
  }
  if (sending) {
    return (
      <div className="flex items-center gap-2 text-sm text-secondary-500">
        <Sparkles size={13} className="text-primary-500 animate-pulse" />
        thinking…
        {showTraces && pendingJob?.traces?.length > 0 && <ToolTraceStack liveTraces={pendingJob.traces} liveBelt={pendingJob.belt} />}
      </div>
    );
  }
  return null;
}

/** Server-side dictation fallback for browsers without SpeechRecognition — the
 *  embed twin of ChatPanel's closure (key-scoped route, its own rate limit). */
async function transcribeEmbedAudio(blob, key, agentKey) {
  const formData = new FormData();
  formData.append('key', key);
  formData.append('agentKey', agentKey);
  formData.append('audio', blob, 'dictation.webm');
  const r = await fetch('/api/embed/stt/transcribe', { method: 'POST', body: formData });
  if (!r.ok) {
    // Carry the status + server message so the voice loop can say WHY it stopped
    // (424 = no STT key, 429 = rate limit) instead of a bare failure.
    const body = await r.json().catch(() => ({}));
    const err = new Error(body.error || 'transcribe_unavailable');
    err.status = r.status;
    throw err;
  }
  const data = await r.json();
  return data && data.text;
}

/** LLM transcript correction for the embed widget — the key-scoped twin of
 *  the internal /api/agent/stt/correct route. */
async function correctEmbedAudio(text, key, agentKey) {
  const r = await fetch('/api/embed/stt/correct', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, agentKey, text }),
  });
  if (!r.ok) throw new Error('correct_unavailable');
  const data = await r.json();
  return data && data.text ? data.text : text;
}

/** Prompt optimization for the embed widget — the key-scoped twin of the
 *  internal /api/agent/prompt/optimize route. Fail-soft server-side. */
async function optimizeEmbedPrompt(draftText, key, agentKey) {
  const r = await fetch('/api/embed/prompt/optimize', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, agentKey, text: draftText }),
  });
  if (!r.ok) throw new Error('optimize_unavailable');
  const data = await r.json();
  return data && data.text ? data.text : draftText;
}

/** Avatar state for the header character — 'thinking' only while a turn is actively
 *  running (not while paused waiting for the visitor to answer an ask_user card). */
function characterStateFor({ sending, waitingForUser, speaking }) {
  if (sending && !waitingForUser) return 'thinking';
  if (speaking) return 'talking';
  return 'idle';
}

/** First screen of a brand-new conversation: the key's greeting, so the widget never opens
 *  as an empty box with no indication of what this agent is for. */
function EmptyState({ greeting }) {
  if (!greeting) return null;
  return (
    <div className="flex gap-2 max-w-[92%]">
      <div className="w-6 h-6 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center shrink-0 mt-0.5">
        <Sparkles size={13} />
      </div>
      <div className="rounded-2xl rounded-tl-md px-3.5 py-2.5 bg-[var(--app-bg)] border border-[var(--app-border)] text-sm text-[var(--app-text)]">
        {greeting}
      </div>
    </div>
  );
}

/** The key's saved branding is the floor; the host page's configure() call layers on top.
 *  Resolved in one place so the JSX never re-derives a fallback chain per render. */
function resolveView(branding, hostConfig, agent) {
  const b = branding || {};
  const config = hostConfig || {};
  return {
    title: config.title || b.title || agent?.name || 'Assistant',
    subtitle: b.subtitle || null,
    logoUrl: config.logo || b.logo_url || null,
    greeting: b.greeting || null,
    placeholder: b.placeholder || 'Type a message… (paste an image to attach it)',
    prompts: b.suggested_prompts || [],
    showFooter: config.showFooter === undefined ? !b.hide_branding : config.showFooter,
    allowExpand: b.allow_expand !== false,
    feedback: b.feedback !== false,
    showTraces: b.show_traces !== false,
    voiceInput: b.voice_input !== false,
  };
}

export default function EmbedChat() {
  const { agentKey } = useParams();
  const [params] = useSearchParams();
  const key = params.get('key');
  // End-user identity JWT from the host page (Embed SDK v2, embed.js `data-identity` /
  // identify()) — verified server-side; sessions bind per-user when present.
  const identity = params.get('identity');
  const {
    status, agent, branding, endUser, hostOrigin, messages, sending, pendingJob,
    sessionId, lastError, feedback, voteFeedback, commentFeedback, fetchTraces,
    approvalMode, setApprovalMode,
    send, answerQuestion, submitToolResults, setClientTools, setPageContext,
    uploadAttachment, clearChat, fileHrefFor,
  } = useEmbedChat({ key, agentKey, identity });
  const waitingForUser = pendingJob?.status === 'waiting_for_user' && !!pendingJob.question;

  // Runtime overrides from the host's AgentWidget.configure(). Read through a fixed key
  // list in the bridge, then layered over the server-normalized branding — the key's
  // saved config remains the floor, the page can only restyle on top of it.
  const [hostConfig, setHostConfig] = useState(null);
  const bottomRef = useRef(null);

  // Host-page SDK bridge (client/public/embed.js): `send` nudges in, client-provided tool
  // calls out and their results back. The hook owns the single message listener and its
  // origin check — see its comment for why that check matters more now than it did when
  // `send` was the only inbound message.
  const {
    awaitingConfirm, decideConfirm, hostMobile, hasFeature,
    requestResize, requestClose, publishSessionId, publishError, publishBranding,
  } = useClientToolBridge({
    status,
    hostOrigin,
    pendingJob,
    onSend: useCallback((text) => send(text), [send]),
    onResults: useCallback((results) => submitToolResults(results), [submitToolResults]),
    onToolsToken: useCallback((token) => setClientTools(token), [setClientTools]),
    onPageContext: useCallback((context) => setPageContext(context), [setPageContext]),
    onConfig: useCallback((config) => setHostConfig((current) => ({ ...current, ...config })), []),
  });
  // Memoized for the same reason as ChatPanel.jsx's twins: fresh closures per render
  // made `speak`/`startCapture` unstable and starved the voice loop's re-arm timer,
  // so the mic was never requested (see lib/useVoiceMode.js's loop effect).
  const fetchVoiceStatus = useCallback(
    () => fetch(`/api/embed/voice-settings?key=${encodeURIComponent(key)}&agentKey=${encodeURIComponent(agentKey)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('status_unavailable')))),
    [key, agentKey],
  );
  const synthesize = useCallback((text) => fetch('/api/embed/tts/synthesize', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, agentKey, text }),
  }).then((r) => (r.ok ? r.json() : Promise.reject(new Error('synth_unavailable')))), [key, agentKey]);
  const { supported: speechSupported, speaking, mouthOpen, muted, toggleMuted, speak, cancel, serverSttReady } = useCharacterSpeech({
    fetchStatus: fetchVoiceStatus,
    synthesize,
  });
  // Voice mode (speak-to-chat) — the embed twin of ChatPanel.jsx's wiring. Same
  // auth-agnostic hook; every action is injected over this surface's own key-scoped
  // routes (the STT closure above, TTS above). Voice-command patches (retrieval off,
  // tools off) ride the next send as turnOpts; approval commands flip the same
  // approvalMode state ChatActionsMenu's toggle drives (fail-closed rules server-side
  // unchanged — destructive gated calls deny on this unattended surface regardless).
  // consumePendingPatch only exists once the hook has returned — reach it via a ref
  // so the callback itself stays stable.
  const voiceHookRef = useRef(null);
  const voiceSend = useCallback(
    (text) => send(text, undefined, voiceHookRef.current?.consumePendingPatch()),
    [send],
  );
  const voiceCorrect = useCallback((text) => correctEmbedAudio(text, key, agentKey), [key, agentKey]);
  const voiceTranscribe = useCallback((blob) => transcribeEmbedAudio(blob, key, agentKey), [key, agentKey]);
  // Prompt optimization (composer Sparkles button) — key-scoped twin of ChatPanel's.
  const optimizePrompt = useCallback((draftText) => optimizeEmbedPrompt(draftText, key, agentKey), [key, agentKey]);
  const voiceModeHook = useVoiceMode({
    speak,
    cancel,
    send: voiceSend,
    answerQuestion,
    correct: voiceCorrect,
    pendingJob,
    approvalMode,
    setApprovalMode,
    transcribe: voiceTranscribe,
    serverSttReady,
    sending,
    speaking,
  });
  voiceHookRef.current = voiceModeHook;
  const prevSendingRef = useRef(sending);

  const view = useMemo(() => resolveView(branding, hostConfig, agent), [branding, hostConfig, agent]);

  // Per-key white-labeling reaches every `primary-*` class in the widget by rewriting the
  // brand CSS variables, rather than being painted onto one header title — so bubbles,
  // chips and the send button all pick the customer's colour up too.
  useEffect(() => {
    if (branding?.color_scale) applyBrand(branding.color_scale);
  }, [branding]);

  useEffect(() => {
    const theme = hostConfig?.theme;
    if (theme === 'dark' || theme === 'light') document.documentElement.setAttribute('data-theme', theme);
  }, [hostConfig]);

  // AgentWidget.voice.enable()/disable() — the host expresses intent, the speech hook only
  // exposes a toggle, so reconcile rather than assign.
  useEffect(() => {
    if (hostConfig?.tts === undefined) return;
    if (hostConfig.tts === muted) toggleMuted();
  }, [hostConfig, muted, toggleMuted]);

  const { expanded, canExpand, toggleExpand } = useHostSync({
    key, agentKey, branding, sessionId, lastError, allowExpand: view.allowExpand,
    hasFeature, hostMobile, requestResize, publishSessionId, publishError, publishBranding,
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, awaitingConfirm]);

  // Same "speak on the sending:true->false edge" pattern as ChatPanel.jsx (internal
  // chat) — see that file's comment for why diffing messages by id was dropped.
  // No session-switch guard needed here: unlike the internal chat's agent picker,
  // one EmbedChat mount is pinned to a single agentKey for its whole lifetime.
  useEffect(() => {
    const wasSending = prevSendingRef.current;
    prevSendingRef.current = sending;
    if (!wasSending || sending) return;
    const last = messages[messages.length - 1];
    if (last?.role === 'assistant') speak(last.content);
  }, [sending, messages, speak]);

  useEffect(() => {
    if (sending) cancel();
  }, [sending, cancel]);

  if (status === 'loading') return null;

  if (status === 'unavailable') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[var(--app-bg)]">
        <Card className="w-full max-w-sm p-6">
          <Alert variant="error" title="Chat unavailable">
            This chat can&apos;t be loaded right now.
          </Alert>
        </Card>
      </div>
    );
  }

  const characterState = characterStateFor({ sending, waitingForUser, speaking });
  const greetName = endUser?.name ? endUser.name.split(' ')[0] : null;
  // Starters are the way in when there is nothing to reply to yet, and the obvious next
  // move once an answer has landed — but noise in the middle of a turn.
  const showFeedback = view.feedback;
  const showTraces = view.showTraces;
  const showPrompts = !!view.prompts.length && !sending && !waitingForUser && !awaitingConfirm.length;

  return (
    <div className="h-screen flex flex-col bg-[var(--app-surface)] text-[var(--app-text)]">
      <EmbedHeader
        title={view.title}
        subtitle={greetName ? `Signed in as ${greetName}` : view.subtitle}
        logoUrl={view.logoUrl}
        characterState={characterState}
        mouthOpen={mouthOpen}
        expanded={expanded}
        onExpand={canExpand ? toggleExpand : null}
        onClose={hasFeature('close') ? requestClose : null}
      >
        {serverSttReady && view.voiceInput && (
          <button
            type="button"
            onClick={() => voiceModeHook.toggleVoiceMode()}
            title={voiceModeHook.voiceMode ? 'End voice mode' : 'Start voice mode (speak to chat)'}
            aria-label={voiceModeHook.voiceMode ? 'End voice mode' : 'Start voice mode'}
            aria-pressed={voiceModeHook.voiceMode}
            className={`p-1.5 rounded-lg shrink-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${voiceModeHook.voiceMode ? 'text-white bg-red-500 hover:bg-red-600' : 'text-secondary-500 hover:text-[var(--app-text)] hover:bg-[var(--app-border)]/40'}`}
          >
            {voiceModeHook.voiceMode ? <PhoneOff size={16} /> : <PhoneCall size={16} />}
          </button>
        )}
      </EmbedHeader>
      {voiceModeHook.voiceMode && <VoiceDock state={voiceModeHook.dockState} onHangUp={voiceModeHook.toggleVoiceMode} />}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
        {!messages.length && <EmptyState greeting={view.greeting} />}
        {messages.map((m, i) => (
          <MessageBubble
            key={m.id || i}
            message={m}
            downloadUrl={m.file ? fileHrefFor(m.file.id) : null}
            actions={m.role === 'assistant' ? (
              <MessageActions
                message={m}
                feedback={feedback[m.id]}
                trace={showTraces && m.toolCallCount ? (
                  <ToolTraceStack
                    sessionId={sessionId} messageId={m.id} count={m.toolCallCount}
                    fetchTraces={fetchTraces}
                  />
                ) : null}
                onVote={showFeedback ? (rating) => voteFeedback(m.id, rating) : null}
                onComment={(comment) => commentFeedback(m.id, comment)}
              />
            ) : null}
          />
        ))}
        <PendingArea
          waitingForUser={waitingForUser} pendingJob={pendingJob} sending={sending} showTraces={showTraces}
          onAnswer={answerQuestion} awaitingConfirm={awaitingConfirm} onDecideConfirm={decideConfirm}
        />
        <div ref={bottomRef} />
      </div>
      {showPrompts && <SuggestedPrompts prompts={view.prompts} onPick={send} disabled={sending} />}
      <Composer
        sending={sending} onSend={send} onUploadAttachment={uploadAttachment}
        placeholder={view.placeholder} downloadUrlFor={fileHrefFor}
        allowVoiceInput={view.voiceInput}
        optimizePrompt={optimizePrompt}
        serverDictation={serverSttReady ? {
          supported: true,
          transcribe: (blob) => transcribeEmbedAudio(blob, key, agentKey),
        } : undefined}
        menu={(
          <ChatActionsMenu
            approvalMode={approvalMode} onToggleApprovalMode={setApprovalMode}
            onClearChat={clearChat} hasMessages={messages.length > 0}
            extraItems={speechSupported ? [{
              key: 'mute',
              label: muted ? 'Unmute agent voice' : 'Mute agent voice',
              icon: muted ? VolumeX : Volume2,
              onClick: toggleMuted,
            }] : []}
          />
        )}
      />
      {view.showFooter && (
        <div className="px-3 pb-2 text-[11px] text-secondary-500 text-center">
          AI-generated responses — verify important information.
        </div>
      )}
    </div>
  );
}
